import { Component, EventEmitter, NgZone, OnDestroy, OnInit } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpEventType, HttpRequest, HttpResponse } from '@angular/common/http';
import { last, map } from 'rxjs/operators';
import { Button, ButtonColor, ButtonSize } from 'app/store/models/button.model';
import { DialogService } from 'app/shared/ui/dialog/dialog.service';
import { FormControl, FormGroup } from '@angular/forms';
import { GooglePickerService } from './pickers/google-picker.service';

export const PICKER_GOOGLE_DRIVE = 0x0001;

export class FileUploadModel {
  data: File;
  index: string;
  isValid: boolean;
}

@Component({
  selector: 'app-file-uploader',
  templateUrl: './file-uploader.component.html',
  styleUrls: ['./file-uploader.component.scss'],
  providers: [GooglePickerService],
})
export class FileUploaderComponent implements OnInit, OnDestroy {
  /** Link text */
  buttonLabel = 'Upload';
  /** Name used in form which will be sent in HTTP request. */
  param: string;
  /** Target URL for file uploading. */
  requestUri: string;
  /** File extension that accepted, same as 'accept' of <input type="file" />. By the default, it's set to 'image/*'. */
  accept: string | string[];
  /** Allow you to configure drag and drop area shown or not. */
  showDropZone: boolean;

  allowMultiple: boolean;

  maxFiles: number;

  maxFileSize: string | number;

  extraParams: { [key: string]: any };
  /** Allow you to add handler after its completion. Bubble up response text from remote. */
  complete = new EventEmitter<any>();

  error = new EventEmitter<HttpErrorResponse>();

  uploadStarted: EventEmitter<any> = new EventEmitter<any>();

  showWarnings = true;

  customWarning: string;
  uploadInProgress: boolean;

  uploadFileButtonProperties: Button = {
    color: ButtonColor.BLUE,
    text: 'Upload file(s)',
    size: ButtonSize.SMALL,
    onClick: () => this.uploadFiles(),
  };

  files: Array<FileUploadModel> = [];

  fileUploaded: { success: FileUploadModel[]; error: HttpErrorResponse[] } = {
    success: [],
    error: [],
  };

  uploadProgress: number;

  inputForm: FormGroup;
  availablePickers = 0;
  googleDriveReady = false;
  pickerInProgress = false;
  pickerError = '';
  private destroyed = false;
  private nextFileIndex = 0;

  constructor(
    private httpClient: HttpClient,
    private dialogService: DialogService,
    private googlePicker: GooglePickerService,
    private zone: NgZone,
  ) {
  }

  getMaxFileSizeInBytes() {
    if (!this.maxFileSize) {
      return 0;
    }
    if (typeof this.maxFileSize === 'number') {
      return this.maxFileSize;
    }

    const base = 1024;

    let index = 1;
    for (const target of ['KB', 'MB', 'GB']) {
      if (this.maxFileSize.indexOf(target) !== -1) {
        const size = +this.maxFileSize.slice(
          0,
          this.maxFileSize.indexOf(target),
        );

        return size * Math.pow(base, index);
      }
      index++;
    }

    return 0;
  }

  hasFileWithMaxFileSizeReached(): boolean {
    return this.files.some(file => this.isMaxFileSizeReached(file.data));
  }

  isMaxFileSizeReached(file: File): boolean {
    if (this.maxFileSize) {
      return this.getMaxFileSizeInBytes() < file.size;
    }

    return false;
  }

  ngOnInit() {
    if (this.isGoogleDriveActive()) {
      this.initializeGoogleDrive();
    }
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.googlePicker.close();
  }

  isGoogleDriveActive(): boolean {
    return (this.availablePickers & PICKER_GOOGLE_DRIVE) !== 0 && this.googlePicker.isConfigured();
  }

  initializeGoogleDrive(): void {
    this.pickerError = '';
    this.googlePicker.initialize().then(() => this.zone.run(() => {
      this.googleDriveReady = true;
    }), error => this.zone.run(() => {
      this.pickerError = error.message || 'Unable to load Google Drive. Please check your connection and try again.';
    }));
  }

  selectGoogleDriveFiles(): void {
    if (!this.googleDriveReady || this.pickerInProgress || this.uploadInProgress || this.isFilesLimitReached()) {
      return;
    }
    this.pickerInProgress = true;
    this.pickerError = '';
    this.googlePicker.createPicker({
      accept: this.accept,
      maxFiles: !this.allowMultiple ? 1 : (this.maxFiles ? this.maxFiles - this.files.length : 0),
      maxFileSize: this.getMaxFileSizeInBytes(),
    }).then(files => this.zone.run(() => {
      if (!this.destroyed) {
        files.forEach(file => this.addFile(file));
      }
      this.pickerInProgress = false;
    }), error => this.zone.run(() => {
      this.pickerError = error.message || 'Unable to download files from Google Drive. Please try again.';
      this.pickerInProgress = false;
    }));
  }

  private addFile(file: File): void {
    if (!file || this.isFilesLimitReached()) {
      return;
    }
    const index = `file_${this.nextFileIndex++}`;
    this.inputForm?.addControl(index, new FormControl(file));
    this.files.push({ index, data: file, isValid: this.isFileValid(file) && !this.isMaxFileSizeReached(file) });
  }

  isAllowerFilesLimitReached() {
    if (this.maxFiles) {
      return this.maxFiles <= this.files.length;
    }

    return false;
  }

  isFilesLimitReached(): boolean {
    if (!this.allowMultiple) {
      return this.files.length >= 1;
    }

    return this.isAllowerFilesLimitReached();
  }

  onClick() {
    if (this.pickerInProgress || this.uploadInProgress) {
      return;
    }
    const fileUpload = document.getElementById(
      'fileUpload',
    ) as HTMLInputElement;
    fileUpload.onchange = () => {
      for (let index = 0; index < fileUpload.files.length; index++) {
        if (this.isFilesLimitReached()) {
          continue;
        }
        this.addFile(fileUpload.files[index]);
      }
      fileUpload.value = '';
    };
    fileUpload.click();
  }

  cancelFile(file: FileUploadModel) {
    if (file) {
      this.removeFileFromArray(file);
    }
  }

  getAcceptedExtensions() {
    if (Array.isArray(this.accept)) {
      return this.accept.join(',');
    }

    return this.accept;
  }

  hasValidFiles(): boolean {
    return this.files.filter(file => file.isValid === true).length > 0;
  }

  isFileValid(file: File): boolean {
    const accepted = (this.getAcceptedExtensions() || '').toLowerCase().split(',')
      .map(value => value.trim()).filter(Boolean);
    const name = file.name.toLowerCase();
    const type = file.type.toLowerCase();
    return !accepted.length || accepted.some(value =>
      value === '*/*' ||
      (value.startsWith('.') && name.endsWith(value)) ||
      (value.endsWith('/*') && type.startsWith(value.slice(0, -1))) ||
      type === value ||
      // CSV MIME types vary between browsers and Drive uploads.
      (value === 'text/csv' && name.endsWith('.csv')),
    );
  }

  dropHandler(ev: DragEvent) {
    // Prevent default behavior (Prevent file from being opened)
    ev.preventDefault();
    if (this.pickerInProgress || this.uploadInProgress) {
      return;
    }

    if (ev.dataTransfer.items) {
      // Use DataTransferItemList interface to access the file(s)
      for (let i = 0; i < ev.dataTransfer.items.length; i++) {
        if (this.isFilesLimitReached()) {
          continue;
        }
        // If dropped items aren't files, reject them
        if (ev.dataTransfer.items[i].kind === 'file') {
          const file = ev.dataTransfer.items[i].getAsFile();

          this.addFile(file);
        }
      }
    } else {
      // Use DataTransfer interface to access the file(s)
      for (let i = 0; i < ev.dataTransfer.files.length; i++) {
        if (this.isFilesLimitReached()) {
          continue;
        }
        this.addFile(ev.dataTransfer.files[i]);
      }
    }
  }

  dragOverHandler(ev: DragEvent) {
    // Prevent default behavior (Prevent file from being opened)
    ev.preventDefault();
  }

  private uploadFiles() {
    if (this.pickerInProgress || this.uploadInProgress) {
      return;
    }
    const fileUpload = document.getElementById(
      'fileUpload',
    ) as HTMLInputElement;
    fileUpload.value = '';

    if (!this.hasValidFiles()) {
      return;
    }

    const fd = new FormData();
    for (const file of this.files) {
      if (!file.isValid) {
        continue;
      }

      fd.append(`${this.param}`, file.data, file.data.name);
    }

    if (this.extraParams && Object.keys(this.extraParams).length > 0) {
      for (const key in this.extraParams) {
        if (this.extraParams[key]) {
          fd.append(key, this.extraParams[key]);
        }
      }
    }

    this.uploadStarted.next(true);
    this.uploadStarted.complete();

    const req = new HttpRequest('POST', this.requestUri, fd, {
      reportProgress: true,
    });

    this.uploadInProgress = true;
    this.httpClient
      .request(req)
      .pipe(
        map(event => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              this.uploadProgress = Math.round(
                (event.loaded * 100) / event.total,
              );
              break;
            case HttpEventType.Response:
              return event;
          }
        }),
        last(),
      )
      .subscribe(
        (response: HttpResponse<any>) => {
          this.complete.next(response.body);
          this.uploadInProgress = false;
        },
        error => {
          this.error.next(error);
          this.uploadInProgress = false;
        },
      );
  }

  private removeFileFromArray(file: FileUploadModel) {
    const index = this.files.indexOf(file);
    if (index > -1) {
      this.files.splice(index, 1);
      this.inputForm?.removeControl(file.index);
    }
  }
}
